import React, { useState } from "react";

const GetStartedForm = () => {
	// Update state to match the required JSON format
	const [formData, setFormData] = useState({
		firstname: "",
		lastname: "",
		email: "",
		care_type: "",
		other_care_type: "",
		location_preference: "",
		preferred_city: "",
		availability_days: "",
		availability_times: "",
		accommodations: "",
		transportation: "",
		urgency: "",
		additional_preferences: "",
		has_insurance: false,
		insurance_type: "",
		consent: false,
	});
	const [errors, setErrors] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitMessage, setSubmitMessage] = useState("");

	// Validation functions
	const validateEmail = (email) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	// Update validation to match new form structure
	const validateForm = () => {
		const newErrors = {};

		// Basic information validations
		if (!formData.firstname.trim()) {
			newErrors.firstname = "First name is required";
		}

		if (!formData.lastname.trim()) {
			newErrors.lastname = "Last name is required";
		}

		if (!formData.email) {
			newErrors.email = "Email is required";
		} else if (!validateEmail(formData.email)) {
			newErrors.email = "Please enter a valid email";
		}

		// Care type validation
		if (!formData.care_type) {
			newErrors.care_type = "Please select type of care";
		}

		// Location validation
		if (!formData.preferred_city.trim()) {
			newErrors.preferred_city = "Please specify your preferred city";
		}

		// Availability validation
		if (!formData.availability_days) {
			newErrors.availability_days = "Please select your available days";
		}

		if (!formData.availability_times) {
			newErrors.availability_times = "Please select your available times";
		}

		// Accommodations validation
		if (!formData.accommodations) {
			newErrors.accommodations =
				"Please select at least one accommodation option";
		}

		// Transportation validation
		if (!formData.transportation) {
			newErrors.transportation = "Please select a transportation option";
		}

		// Urgency validation
		if (!formData.urgency) {
			newErrors.urgency = "Please specify urgency";
		}

		// Insurance validation
		if (formData.has_insurance && !formData.insurance_type) {
			newErrors.insurance_type = "Please specify insurance type";
		}

		// Consent validation
		if (!formData.consent) {
			newErrors.consent = "You must consent to proceed";
		}

		return newErrors;
	};

	// Update input handler to handle new form structure
	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target;

		setFormData((prev) => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));

		// Clear error when user types
		if (errors[name]) {
			setErrors((prev) => ({ ...prev, [name]: "" }));
		}
	};

	// Handle form submission
	const handleSubmit = async (e) => {
		e.preventDefault();

		// Validate form
		const newErrors = validateForm();
		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		setIsSubmitting(true);
		try {
			const response = await fetch(
				"https://api.dev.synergycarepro.com/api/v1/patient/patient-b/submit-questionnaire/",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify(formData),
				}
			);
			console.log(formData);
			const responseData = await response.json();

			if (response.ok) {
				setSubmitMessage("Thank you! We'll be in touch soon.");
				setFormData({
					firstname: "",
					lastname: "",
					email: "",
					care_type: "",
					other_care_type: "",
					location_preference: "",
					preferred_city: "",
					availability_days: "",
					availability_times: "",
					accommodations: "",
					transportation: "",
					urgency: "",
					additional_preferences: "",
					has_insurance: false,
					insurance_type: "",
					consent: false,
				});

				// Navigate to the signup page with the patient ID
				if (responseData?.success) {
					window.location.href = `https://staging.synergycarepro.com/referrals/signup?id=${responseData?.data?.patient}`;
				}
			} else {
				setSubmitMessage("Something went wrong. Please try again.");
			}
		} catch (error) {
			console.error("Error submitting form:", error);
			setSubmitMessage("Failed to submit. Please try again later.");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<div className='bg-[#F0FFF8]' id='form'>
				<div className='max-w-[652px] xl:w-[80%] w-[90%] mx-auto py-16 space-y-10 text-[#1D1D1D]'>
					<h2 className='lg:text-4xl/[40px] text-3xl/[36px] text-center font-medium capitalize'>
						You are one step away from access to great healthcare and social
						benefits
					</h2>

					<div className='space-y-8'>
						<p className='text-2xl leading-9 font-normal capitalize'>
							Enter your personal information below
						</p>

						{/* First Name Input */}
						<div className='relative'>
							<input
								type='text'
								name='firstname'
								value={formData.firstname}
								onChange={handleInputChange}
								placeholder='First Name*'
								className={`w-full border-b bg-inherit border-gray-300 focus:outline-none focus:border-green-500 py-2 text-gray-700 placeholder-gray-500 ${
									errors.firstname ? "border-red-500" : ""
								}`}
							/>
							{errors.firstname && (
								<p className='text-red-500 text-sm mt-1'>{errors.firstname}</p>
							)}
						</div>

						{/* Last Name Input */}
						<div className='relative'>
							<input
								type='text'
								name='lastname'
								value={formData.lastname}
								onChange={handleInputChange}
								placeholder='Last Name*'
								className={`w-full border-b bg-inherit border-gray-300 focus:outline-none focus:border-green-500 py-2 text-gray-700 placeholder-gray-500 ${
									errors.lastname ? "border-red-500" : ""
								}`}
							/>
							{errors.lastname && (
								<p className='text-red-500 text-sm mt-1'>{errors.lastname}</p>
							)}
						</div>

						{/* Email Address Input */}
						<div className='relative'>
							<input
								type='email'
								name='email'
								value={formData.email}
								onChange={handleInputChange}
								placeholder='Email Address*'
								className={`w-full border-b bg-inherit focus:outline-none focus:border-green-500 py-2 text-gray-700 placeholder-gray-500 ${
									errors.email ? "border-red-500" : ""
								}`}
							/>
							{errors.email && (
								<p className='text-red-500 text-sm mt-1'>{errors.email}</p>
							)}
						</div>
					</div>
				</div>
			</div>

			<div className='max-w-[814px] lg:w-full w-[90%] mx-auto py-16 space-y-10'>
				<div className='text-[#11112D] text-center space-y-[10px]'>
					<h2 className='text-5xl lg:text-[64px] leading-[50px] lg:leading-[76px] font-medium tracking-[-3px]'>
						Patient Support Survey
					</h2>
					<p className='text-2xl leading-10 font-normal'>
						Please answer the following questions to help us understand your
						needs and how we can best assist you:
					</p>
				</div>

				<div className='space-y-10 max-w-[768px] w-full mx-auto'>
					<label className='block text-xl font-semibold'>
						1. What type of care are you seeking?
					</label>
					<select
						name='care_type'
						value={formData.care_type}
						onChange={handleInputChange}
						className={`w-full border-b bg-inherit border-gray-300 focus:outline-none focus:border-green-500 py-2 text-gray-700 placeholder-gray-500 ${
							errors.care_type ? "border-red-500" : ""
						}`}>
						<option value=''>Select an option</option>
						<option value='General health check-up'>
							General health check-up
						</option>
						<option value='Specialist care'>Specialist care</option>
						<option value='Diagnostic services'>Diagnostic services</option>
						<option value='Preventive care'>Preventive care</option>
						<option value='Other'>Other</option>
					</select>
					{errors.care_type && (
						<p className='text-red-500 text-sm'>{errors.care_type}</p>
					)}

					{formData.care_type === "Other" && (
						<div className='relative mt-2'>
							<input
								type='text'
								name='other_care_type'
								value={formData.other_care_type}
								onChange={handleInputChange}
								placeholder='Please specify other care type*'
								className='w-full border-b bg-inherit border-gray-300 focus:outline-none focus:border-green-500 py-2 text-gray-700 placeholder-gray-500'
							/>
						</div>
					)}

					<label className='block text-xl font-semibold'>
						2. Do you have any preferences regarding the clinic location?
					</label>
					<div className='flex items-center flex-wrap gap-x-5'>
						<span className='text-nowrap'>Preferred city or area </span>
						<input
							type='text'
							name='preferred_city'
							value={formData.preferred_city}
							onChange={handleInputChange}
							className={`w-full border-b bg-inherit border-gray-300 focus:outline-none focus:border-green-500 py-2 text-gray-700 placeholder-gray-500 ${
								errors.preferred_city ? "border-red-500" : ""
							}`}
						/>
					</div>

					<label className='block text-xl font-semibold'>
						3. What is your availability?
					</label>

					{/* Days Selection */}
					<div className='space-y-4'>
						<p className='text-base font-medium'>Available Days:</p>
						<div className='flex flex-wrap gap-6'>
							{["Weekdays", "Weekends"].map((day) => (
								<label key={day} className='flex items-center'>
									<input
										type='radio'
										name='availability_days'
										value={day}
										checked={formData.availability_days === day}
										onChange={handleInputChange}
										className='mr-2'
									/>
									{day}
								</label>
							))}
						</div>
						{errors.availability_days && (
							<p className='text-red-500 text-sm'>{errors.availability_days}</p>
						)}
					</div>

					{/* Times Selection */}
					<div className='space-y-4'>
						<p className='text-base font-medium text'>Preferred Times:</p>
						<div className='flex flex-wrap gap-6'>
							{[
								{
									value: "Morning (8 AM–12 PM)",
									label: "Morning (8 AM–12 PM)",
								},
								{
									value: "Afternoon (12 PM–4 PM)",
									label: "Afternoon (12 PM–4 PM)",
								},
								{ value: "Evening (4 PM–8 PM)", label: "Evening (4 PM–8 PM)" },
							].map((time) => (
								<label key={time.value} className='flex items-center'>
									<input
										type='radio'
										name='availability_times'
										value={time.value}
										checked={formData.availability_times === time.value}
										onChange={handleInputChange}
										className='mr-2'
									/>
									{time.label}
								</label>
							))}
						</div>
						{errors.availability_times && (
							<p className='text-red-500 text-sm'>
								{errors.availability_times}
							</p>
						)}
					</div>

					{/* Accommodations Selection */}
					<label className='block text-xl font-semibold'>
						4. Do you require any of the following accommodations?
					</label>
					<div className='space-y-4'>
						<div className='flex flex-col gap-3'>
							{[
								{
									value: "Wheelchair accessibility",
									label: "Wheelchair accessibility",
								},
								{
									value: "Gender-specific care providers",
									label: "Gender-specific care providers",
								},
								{
									value: "Translation or interpretation services",
									label: "Translation or interpretation services",
								},
								{
									value: "Child-friendly facilities",
									label: "Child-friendly facilities",
								},
								{ value: "None", label: "None" },
							].map((accommodation) => (
								<label key={accommodation.value} className='flex items-center'>
									<input
										type='radio'
										name='accommodations'
										value={accommodation.value}
										checked={formData.accommodations === accommodation.value}
										onChange={handleInputChange}
										className='mr-2'
									/>
									{accommodation.label}
								</label>
							))}
						</div>
						{errors.accommodations && (
							<p className='text-red-500 text-sm'>{errors.accommodations}</p>
						)}
					</div>

					{/* Transportation Selection */}
					<label className='block text-xl font-semibold'>
						5. What transportation option is most convenient for you?
					</label>
					<div className='space-y-4'>
						<div className='flex flex-col gap-3'>
							{[
								{
									value: "I can travel independently",
									label: "I can travel independently",
								},
								{
									value: "I need transportation assistance",
									label: "I need transportation assistance",
								},
								{
									value: "I prefer a clinic near public transportation options",
									label: "I prefer a clinic near public transportation options",
								},
							].map((option) => (
								<label key={option.value} className='flex items-center'>
									<input
										type='radio'
										name='transportation'
										value={option.value}
										checked={formData.transportation === option.value}
										onChange={handleInputChange}
										className='mr-2'
									/>
									{option.label}
								</label>
							))}
						</div>
						{errors.transportation && (
							<p className='text-red-500 text-sm'>{errors.transportation}</p>
						)}
					</div>

					<label className='block text-xl font-semibold'>
						6. How urgent is your need for care?
					</label>
					<select
						name='urgency'
						value={formData.urgency}
						onChange={handleInputChange}
						className={`w-full border-b bg-inherit border-gray-300 focus:outline-none focus:border-green-500 py-2 text-gray-700 placeholder-gray-500 ${
							errors.urgency ? "border-red-500" : ""
						}`}>
						<option value=''>Select urgency</option>
						<option value='Immediate (within 1-3 days)'>
							Immediate (within 1-3 days)
						</option>
						<option value='Soon (within a week)'>Soon (within a week)</option>
						<option value='Flexible (within a month)'>
							Flexible (within a month)
						</option>
					</select>

					<label className='block text-xl font-semibold'>
						7. Do you have health insurance coverage?
					</label>
					<div className='flex gap-4'>
						{["Yes", "No"].map((option) => (
							<label key={option} className='flex items-center'>
								<input
									type='radio'
									name='has_insurance'
									value={option === "Yes"}
									checked={formData.has_insurance === (option === "Yes")}
									onChange={(e) => {
										setFormData((prev) => ({
											...prev,
											has_insurance: e.target.value === "true",
											insurance_type:
												e.target.value === "true" ? prev.insurance_type : "",
										}));
									}}
									className='mr-2'
								/>
								{option}
							</label>
						))}
					</div>

					{formData.has_insurance && (
						<div className='mt-4'>
							<label className='block text-xl font-semibold mb-2'>
								What type of insurance do you have?
							</label>
							<select
								name='insurance_type'
								value={formData.insurance_type}
								onChange={handleInputChange}
								className={`w-full border-b bg-inherit border-gray-300 focus:outline-none focus:border-green-500 py-2 text-gray-700 placeholder-gray-500 ${
									errors.insurance_type ? "border-red-500" : ""
								}`}>
								<option value=''>Select insurance type</option>
								<option value='Private'>Private</option>
								<option value='Medicare'>Medicare</option>
								<option value='Medicaid'>Medicaid</option>
								<option value='Other'>Other</option>
							</select>
							{errors.insurance_type && (
								<p className='text-red-500 text-sm mt-1'>
									{errors.insurance_type}
								</p>
							)}
						</div>
					)}

					{/* Additional Preferences Textarea */}
					<label className='block text-xl font-semibold'>
						8. Do you have any other preferences or details you want to add?
					</label>
					<div className='space-y-2'>
						<textarea
							name='additional_preferences'
							value={formData.additional_preferences}
							onChange={handleInputChange}
							placeholder='Enter any additional preferences or details here...'
							className='w-full min-h-[100px] p-3 border border-gray-300 rounded-md bg-inherit focus:outline-none focus:border-green-500 text-gray-700 placeholder-gray-500'
						/>
					</div>

					{/* Consent Checkbox */}
					<div className='relative flex items-start gap-2'>
						<input
							type='checkbox'
							name='consent'
							checked={formData.consent}
							onChange={handleInputChange}
							className={`mt-1 border-gray-300 rounded ${
								errors.consent ? "border-red-500" : ""
							}`}
						/>
						<label className='text-sm text-gray-600 underline underline-offset-2 decoration-green-500 decoration-[2px]'>
							I consent to SynergyCare Foundation processing my personal
							information
						</label>
						{errors.consent && (
							<p className='text-red-500 text-sm absolute bottom-[-20px] left-0'>
								{errors.consent}
							</p>
						)}
					</div>

					{/* Submit Button */}
					<button
						type='submit'
						disabled={isSubmitting}
						className='w-full bg-green-500 text-white py-3 rounded-full hover:bg-green-600 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed'>
						{isSubmitting ? "Submitting..." : "Submit and Get Connected"}
					</button>
					{/* Submit Message */}
					{submitMessage && (
						<p
							className={`text-base/6 ${
								submitMessage.includes("error")
									? "text-red-500"
									: "text-green-500"
							}`}>
							{submitMessage}
						</p>
					)}
				</div>
			</div>
		</form>
	);
};

export default GetStartedForm;
